import type { BoardObjectV2 } from "@bezpart-board/shared";

export const COMMENTS_META_KEY = "comments";

export const MAX_OBJECT_COMMENTS = 50;
export const MAX_COMMENT_TEXT_LENGTH = 1000;

export type ObjectComment = {
  id: string;
  text: string;
  authorLabel: string;
  authorPeerId?: string;
  createdAt: number;
};

function metaOf(obj: BoardObjectV2): Record<string, unknown> {
  return (obj.meta && typeof obj.meta === "object" ? obj.meta : {}) as Record<string, unknown>;
}

function isCommentEntry(raw: unknown): raw is ObjectComment {
  if (!raw || typeof raw !== "object") return false;
  const c = raw as Record<string, unknown>;
  return (
    typeof c.id === "string" &&
    typeof c.text === "string" &&
    typeof c.authorLabel === "string" &&
    typeof c.createdAt === "number"
  );
}

export function readObjectComments(obj: BoardObjectV2 | null | undefined): ObjectComment[] {
  if (!obj) return [];
  const raw = metaOf(obj)[COMMENTS_META_KEY];
  if (!Array.isArray(raw)) return [];
  return raw.filter(isCommentEntry).slice(0, MAX_OBJECT_COMMENTS);
}

export function objectCommentCount(obj: BoardObjectV2 | null | undefined): number {
  return readObjectComments(obj).length;
}

/** Полный next meta с обновлённым списком комментов (replace bag). */
export function withCommentsMeta(
  obj: BoardObjectV2,
  comments: ObjectComment[]
): Record<string, unknown> | null {
  const next = { ...metaOf(obj) };
  const cleaned = comments
    .filter(isCommentEntry)
    .map((c) => ({
      id: c.id,
      text: c.text.slice(0, MAX_COMMENT_TEXT_LENGTH),
      authorLabel: c.authorLabel.slice(0, 64),
      ...(typeof c.authorPeerId === "string" ? { authorPeerId: c.authorPeerId } : {}),
      createdAt: c.createdAt,
    }))
    .slice(0, MAX_OBJECT_COMMENTS);
  if (cleaned.length) next[COMMENTS_META_KEY] = cleaned;
  else delete next[COMMENTS_META_KEY];
  return Object.keys(next).length ? next : null;
}

export function normalizeCommentText(raw: string): string {
  return raw.replace(/\r\n/g, "\n").trim().slice(0, MAX_COMMENT_TEXT_LENGTH);
}
